import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { useNavigate } from '@wfp-common/hooks/useNavigate';

import { loadProducts, authorizeProduct } from '../apiClient';
import { Product } from './helpers';
import { SortableTableHead } from '../utils/table/table-head/sortable-table-head';
import { ChangeSortOptions, SORT_OPTIONS, SortOptions } from '../utils/hooks/useSort';
import State from '../app/store/state';
import { wfpFormat } from '../utils/utils';
import { hasFeatureAccess } from '../login/auth';
import { ManagerPermission } from '../permission-profiles/permission';
import { successAction, errorAction } from '../utils/notifications';

const columnToAttributeMapping = {
    Product: 'name',
    Category: 'category',
    'Created By': 'createdByManager',
    'Created At': 'createdAt',
    'Authorized At': 'authorizedAt',
    'Quantity Option': 'quantityOption',
    Unit: 'unit',
    Status: 'status',
};

export const ProductsListPage: React.FC = () => {
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [authorizingProducts, setAuthorizingProducts] = useState<Set<number>>(new Set());
    const [sortOptions, setSortOptions] = useState<SORT_OPTIONS>(new SortOptions({ column: 'name', direction: 'ASC' }));
    const tz = useSelector((state: State) => state.appConfig.timeZone);
    const auth = useSelector((state: State) => state.auth);
    const currentManager = useSelector((state: State) => state.auth.manager);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        loadAllProducts();
    }, []);

    const loadAllProducts = async () => {
        try {
            setLoading(true);
            const response = await loadProducts();
            setAllProducts(response);
        } catch (error) {
            // Handle error silently or with proper error handling
        } finally {
            setLoading(false);
        }
    };

    const sortedProducts = useMemo(() => {
        if (!allProducts.length || !sortOptions) return allProducts;

        const sorted = [...allProducts].sort((a, b) => {
            const column = sortOptions['orderBy:column'];
            const direction = sortOptions['orderBy:direction'];
            let aValue = a[column];
            let bValue = b[column];

            if (column === 'createdAt' || column === 'authorizedAt') {
                aValue = aValue ? new Date(aValue).getTime() : 0;
                bValue = bValue ? new Date(bValue).getTime() : 0;
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue?.toLowerCase() || '';
            }

            if (aValue < bValue) return direction === 'ASC' ? -1 : 1;
            if (aValue > bValue) return direction === 'ASC' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [allProducts, sortOptions]);

    const handleSort: ChangeSortOptions = ([column, direction]) => {
        if (!column) column = 'Product';
        const mappedColumn = columnToAttributeMapping[column];
        const options = new SortOptions({ column: mappedColumn, direction });
        setSortOptions(options);
    };

    const handleProductClick = (productId: number) => {
        navigate(`/products/${productId}`);
    };

    const handleAddProduct = () => {
        navigate('/products/add-product');
    };

    const handleAuthorize = async (productId: number) => {
        if (authorizingProducts.has(productId)) return; // Prevent double-click

        setAuthorizingProducts((prev) => new Set(prev).add(productId));

        try {
            await authorizeProduct(productId);
            // Update the local state to reflect the authorization
            setAllProducts((prevProducts) =>
                prevProducts.map((product) =>
                    product.id === productId
                        ? {
                              ...product,
                              authorizedAt: new Date().toISOString(),
                              authorizedByManagerId: Number(currentManager?.id),
                          }
                        : product
                )
            );
            dispatch(successAction('Product authorized successfully'));
        } catch (error) {
            dispatch(errorAction('Failed to authorize product'));
        } finally {
            setAuthorizingProducts((prev) => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        }
    };

    const canAuthorizeProduct = (product: Product): boolean => {
        // Check if manager has permission
        if (!hasFeatureAccess(auth, ManagerPermission.productsCreateUpdate)) {
            return false;
        }

        // Check if product is already authorized
        if (product.authorizedAt) {
            return false;
        }

        // Check if current manager is different from creator
        if (String(product.createdByManagerId) === String(currentManager?.id)) {
            return false;
        }

        return true;
    };

    if (loading) {
        return <div>Loading products...</div>;
    }

    return (
        <div>
            <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}
            >
                <div>
                    <h4>All Products</h4>
                </div>
                {hasFeatureAccess(auth, ManagerPermission.productsCreateUpdate) && (
                    <div>
                        <button className="wfp-btn--primary" onClick={handleAddProduct} type="button">
                            Add Product
                        </button>
                    </div>
                )}
            </div>
            <table className="wfp-table mt4 wfp-table--striped table-hover">
                <SortableTableHead changeSortOptions={handleSort}>
                    <SortableTableHead.Cell columnName="Product">Product</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Category">Category</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Created By">Created By</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Created At">Created At</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Authorized At">Authorized At</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Quantity Option">Quantity Option</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Unit">Unit</SortableTableHead.Cell>
                    <SortableTableHead.Cell columnName="Status">Status</SortableTableHead.Cell>
                </SortableTableHead>
                <tbody>
                    {sortedProducts.map((product) => (
                        <tr className="cursor-pointer" key={product.id} onClick={() => handleProductClick(product.id)}>
                            <td>{product.name}</td>
                            <td>{wfpFormat(product.category)}</td>
                            <td>{product.createdByManager}</td>
                            <td>{moment(product.createdAt).tz(tz).format('DD/MM/YYYY HH:mm')}</td>
                            <td>
                                {product.authorizedAt ? (
                                    moment(product.authorizedAt).tz(tz).format('DD/MM/YYYY HH:mm')
                                ) : canAuthorizeProduct(product) ? (
                                    <button
                                        className="wfp-btn--primary wfp-btn--sm"
                                        disabled={authorizingProducts.has(product.id)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAuthorize(product.id);
                                        }}
                                        type="button"
                                    >
                                        {authorizingProducts.has(product.id) ? 'Authorizing...' : 'Authorize'}
                                    </button>
                                ) : (
                                    '-'
                                )}
                            </td>
                            <td>{wfpFormat(product.quantityOption)}</td>
                            <td>{product.unit}</td>
                            <td>{wfpFormat(product.status)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {sortedProducts.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p>No products found.</p>
                </div>
            )}
        </div>
    );
};
